/**
 * 解析種別ドロップダウン（左ペイン上部）
 *
 * メニューは document.body へポータルし、左レールの overflow:hidden で欠けないようにする。
 * 配置はトグル右（中央作業面）。狭い画面では下方向にフォールバック。
 */

import { Fragment, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Dropdown } from 'react-bootstrap';
import { useShallow } from 'zustand/react/shallow';
import {
  normalizeAnalysisType,
  useKeisukeDashboardStore,
  type AnalysisTypeKey,
} from '../../../../stores/systems/keisuke/dashboardStore';
import { buildAnalysisSwitchPatch } from '../../../../utils/keisukeAnalysisDefaults';
import { gridDataHasNumericValues } from '../../../../utils/keisukeGridData';
import { tTestMethodShortLabelKey } from '../../../../utils/keisukeTTestDisplay';
import styles from './keisukeAnalysisPicker.module.scss';

type AnalysisMenuItem = {
  value: AnalysisTypeKey;
  labelKey: string;
  defaultLabel: string;
  icon: string;
};

export const KEISUKE_ANALYSIS_MENU: {
  categoryKey: string;
  categoryDefault: string;
  items: AnalysisMenuItem[];
}[] = [
  {
    categoryKey: 'keisuke.workspace.category.comparison',
    categoryDefault: '比較検定',
    items: [
      { value: 't-test', labelKey: 'keisuke.tabs.ttest', defaultLabel: 't 検定', icon: 'fa-chart-bar' },
      { value: 'anova', labelKey: 'keisuke.tabs.anova', defaultLabel: 'ANOVA', icon: 'fa-table' },
    ],
  },
  {
    categoryKey: 'keisuke.workspace.category.doseSurvival',
    categoryDefault: '用量・生存',
    items: [
      { value: 'dose-response', labelKey: 'keisuke.tabs.doseResponse', defaultLabel: 'LD50/EC50', icon: 'fa-flask' },
      { value: 'survival', labelKey: 'keisuke.tabs.survivalCurve', defaultLabel: '生存曲線', icon: 'fa-chart-line' },
    ],
  },
  {
    categoryKey: 'keisuke.workspace.category.other',
    categoryDefault: 'その他',
    items: [
      { value: 'basic-stats', labelKey: 'keisuke.tabs.basicStatistics', defaultLabel: '基本統計', icon: 'fa-calculator' },
      { value: 'qpcr', labelKey: 'keisuke.tabs.qpcr', defaultLabel: 'qPCR', icon: 'fa-dna' },
      { value: 'longitudinal', labelKey: 'keisuke.tabs.longitudinal', defaultLabel: '縦断解析', icon: 'fa-clock' },
      { value: 'regression', labelKey: 'keisuke.workspace.regression', defaultLabel: '回帰・検量線', icon: 'fa-chart-scatter' },
      { value: 'bar-chart', labelKey: 'keisuke.tabs.barChart', defaultLabel: '棒グラフ', icon: 'fa-chart-column' },
    ],
  },
];

export function findKeisukeAnalysisLabel(
  type: AnalysisTypeKey,
  t: (k: string) => string
): string {
  for (const cat of KEISUKE_ANALYSIS_MENU) {
    const item = cat.items.find((i) => i.value === type);
    if (item) return t(item.labelKey);
  }
  return type;
}

const MENU_MIN_WIDTH_PX = 224;
const MENU_VIEWPORT_PAD_PX = 8;
const MENU_GAP_PX = 6;

type MenuPos = { top: number; left: number; maxHeight: number };

function placeMenuBesideToggle(toggle: HTMLElement): MenuPos {
  const r = toggle.getBoundingClientRect();
  const maxHeight = Math.min(window.innerHeight * 0.7, 480);
  let left = r.right + MENU_GAP_PX;
  let top = r.top;
  if (left + MENU_MIN_WIDTH_PX > window.innerWidth - MENU_VIEWPORT_PAD_PX) {
    left = Math.max(MENU_VIEWPORT_PAD_PX, r.left);
    top = r.bottom + MENU_GAP_PX;
  }
  if (top + maxHeight > window.innerHeight - MENU_VIEWPORT_PAD_PX) {
    top = Math.max(MENU_VIEWPORT_PAD_PX, window.innerHeight - maxHeight - MENU_VIEWPORT_PAD_PX);
  }
  if (top < MENU_VIEWPORT_PAD_PX) top = MENU_VIEWPORT_PAD_PX;
  return { top, left, maxHeight };
}

export default function KeisukeAnalysisPicker() {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const [menuPos, setMenuPos] = useState<MenuPos>({ top: 0, left: 0, maxHeight: 480 });
  const toggleRef = useRef<HTMLButtonElement | null>(null);

  const {
    analysisOptions,
    gridHeaders,
    gridData,
    oneWayGroupCount,
    setAnalysisOptions,
    setOneWayGroupCount,
  } = useKeisukeDashboardStore(
    useShallow((s) => ({
      analysisOptions: s.analysisOptions,
      gridHeaders: s.gridHeaders,
      gridData: s.gridData,
      oneWayGroupCount: s.oneWayGroupCount,
      setAnalysisOptions: s.setAnalysisOptions,
      setOneWayGroupCount: s.setOneWayGroupCount,
    }))
  );

  const currentType = normalizeAnalysisType(analysisOptions.type);
  const pickerLabel =
    currentType === 't-test'
      ? t(
          tTestMethodShortLabelKey(
            {
              testType: analysisOptions.testType,
              ttestInferential: analysisOptions.ttestInferential,
              equalVariance: analysisOptions.equalVariance === true,
            },
            'picker'
          )
        )
      : findKeisukeAnalysisLabel(currentType, t);

  const selectAnalysis = useCallback(
    (type: AnalysisTypeKey) => {
      const patch = buildAnalysisSwitchPatch(type, gridHeaders, analysisOptions, gridData);
      setAnalysisOptions(patch);
      if (
        type === 'anova' &&
        !gridDataHasNumericValues(gridHeaders, gridData) &&
        oneWayGroupCount > 3
      ) {
        setOneWayGroupCount(3);
      }
      setShow(false);
    },
    [
      analysisOptions,
      gridData,
      gridHeaders,
      oneWayGroupCount,
      setAnalysisOptions,
      setOneWayGroupCount,
    ]
  );

  const updateMenuPos = useCallback(() => {
    const el = toggleRef.current;
    if (!el) return;
    setMenuPos(placeMenuBesideToggle(el));
  }, []);

  useLayoutEffect(() => {
    if (!show) return;
    updateMenuPos();
    window.addEventListener('resize', updateMenuPos);
    window.addEventListener('scroll', updateMenuPos, true);
    return () => {
      window.removeEventListener('resize', updateMenuPos);
      window.removeEventListener('scroll', updateMenuPos, true);
    };
  }, [show, updateMenuPos]);

  const menu =
    show &&
    createPortal(
      <>
        <button
          type="button"
          className={styles.menuBackdrop}
          aria-label={t('keisuke.workspace.closeAnalysisMenu', {
            defaultValue: '解析メニューを閉じる',
          })}
          onClick={() => setShow(false)}
        />
        <div
          className={`dropdown-menu show ${styles.menu}`}
          style={{
            position: 'fixed',
            top: menuPos.top,
            left: menuPos.left,
            maxHeight: menuPos.maxHeight,
            zIndex: 2000,
          }}
          role="menu"
          aria-labelledby="keisuke-analysis-picker"
        >
          {KEISUKE_ANALYSIS_MENU.map((cat) => (
            <Fragment key={cat.categoryKey}>
              <h6 className="dropdown-header">{t(cat.categoryKey)}</h6>
              {cat.items.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  role="menuitem"
                  className={`dropdown-item${currentType === item.value ? ' active' : ''}`}
                  onClick={() => selectAnalysis(item.value)}
                >
                  <i className={`fas ${item.icon} me-2`} aria-hidden />
                  {t(item.labelKey)}
                </button>
              ))}
            </Fragment>
          ))}
        </div>
      </>,
      document.body
    );

  return (
    <div className={styles.wrap} data-keisuke-analysis-picker>
      <Dropdown show={show} onToggle={setShow} className={styles.dropdown} autoClose="outside">
        <Dropdown.Toggle
          ref={toggleRef}
          variant="outline-primary"
          size="sm"
          id="keisuke-analysis-picker"
        >
          <i className="fas fa-flask me-1" aria-hidden />
          {pickerLabel}
        </Dropdown.Toggle>
      </Dropdown>
      {menu}
    </div>
  );
}
